import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, 
  Clock, 
  ArrowRight, 
  Search, 
  Tag, 
  BookOpen, 
  TrendingUp, 
  Users, 
  Star, 
  Filter,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  ChevronRight,
  Flame,
  Zap
} from 'lucide-react';
import { blogs } from '../data/blogs';
import { Button } from '../components/ui/button';
import SEO from '@/components/SEO';
import { Buffer } from 'buffer';

// Buffer polyfill for browser environment
if (typeof window !== 'undefined') {
  // @ts-ignore
  window.Buffer = Buffer;
}

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('latest'); // 'latest', 'popular', 'trending'
  
  // Enhanced blogs data with additional metadata
  const blogsWithSlugs = blogs.map((blog, index) => ({
    ...blog,
    slug: blog.link.split('/').pop(),
    readTime: `${Math.floor(Math.random() * 5) + 3} min read`,
    views: Math.floor(Math.random() * 5000) + 1000,
    likes: Math.floor(Math.random() * 200) + 50,
    comments: Math.floor(Math.random() * 50) + 5,
    category: index === 0 ? 'Quick Commerce' : index === 1 ? 'Local Business' : 'Shopping Tips',
    trending: index < 2,
    featured: index === 0
  }));
  
  // Filter blogs based on search and category
  const filteredBlogs = blogsWithSlugs.filter(blog => {
    if (!blog || !blog.title || !blog.summary) {
      return false;
    }
    
    const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         blog.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         blog.category.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || 
                           blog.category.toLowerCase().includes(selectedCategory.toLowerCase());
    
    return matchesSearch && matchesCategory;
  });

  // Sort blogs
  const sortedBlogs = [...filteredBlogs].sort((a, b) => {
    switch (sortBy) {
      case 'popular':
        return b.views - a.views;
      case 'trending':
        return (b.trending ? 1 : 0) - (a.trending ? 1 : 0);
      default: // latest
        return new Date(b.date).getTime() - new Date(a.date).getTime();
    }
  });

  const categories = [
    { id: 'all', name: 'All Articles', count: blogsWithSlugs.length },
    { id: 'quick commerce', name: 'Quick Commerce', count: 1 },
    { id: 'local business', name: 'Local Business', count: 1 },
    { id: 'shopping', name: 'Shopping Tips', count: 1 }
  ];
  
  const featuredBlog = blogsWithSlugs.find(blog => blog.featured) || blogsWithSlugs[0];

  const blogStructuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Dailzy Blog - Quick Commerce Insights & Stories",
    "description": "Latest insights about quick commerce, local business stories, and the future of instant delivery in India",
    "url": "https://dailzy.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "Dailzy",
      "logo": {
        "@type": "ImageObject",
        "url": "https://dailzy.com/lovable-uploads/8936458b-f120-42aa-a567-eac1e09a8296.png"
      }
    },
    "blogPost": blogsWithSlugs.slice(0, 5).map(blog => ({
      "@type": "BlogPosting",
      "headline": blog.title,
      "description": blog.summary,
      "url": `https://dailzy.com${blog.link}`,
      "datePublished": blog.date,
      "author": {
        "@type": "Organization",
        "name": "Dailzy Team"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Dailzy"
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://dailzy.com${blog.link}`
      }
    }))
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-orange-50">
      <SEO
        title="Dailzy Blog - Quick Commerce Insights & Local Business Stories | Latest Updates"
        description="Discover insights about quick commerce, local business stories, and the future of instant delivery in India. Read about hyperlocal commerce trends, vendor success stories, and industry updates."
        keywords="quick commerce blog, hyperlocal delivery insights, local business stories, ecommerce trends india, instant delivery news, vendor success stories, quick commerce industry"
        url="https://dailzy.com/blog"
        type="website"
        structuredData={blogStructuredData}
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Enhanced Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-80 h-80 bg-red-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
          <div className="absolute bottom-20 right-20 w-72 h-72 bg-orange-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div 
              className="inline-flex items-center gap-2 bg-gradient-to-r from-red-50 to-orange-50 text-red-500 rounded-2xl px-4 py-2 mb-6"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <BookOpen size={16} />
              <span className="font-semibold text-sm">Insights & Stories</span>
            </motion.div>
            
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-6">
              Dailzy <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Blog</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Discover insights about quick commerce, local business stories, and the future of instant delivery in India
            </p>

            {/* Stats Bar */}
          </motion.div>

          {/* Enhanced Search & Filter Section */}
          <motion.div 
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            {/* Search Bar */}
            <div className="relative mb-6">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search articles, topics, or keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent placeholder-gray-500 text-gray-900"
              />
            </div>

            {/* Filters and Sort */}
            <div className="flex flex-wrap gap-4 justify-between items-center">
              {/* Categories */}
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                      selectedCategory === category.id
                        ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-lg'
                        : 'bg-white/80 text-gray-600 hover:bg-white hover:text-red-500 border border-gray-200'
                    }`}
                  >
                    {category.name} ({category.count})
                  </button>
                ))}
              </div>

              {/* Sort Options */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">Sort by:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 bg-white/80 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="latest">Latest</option>
                  <option value="popular">Most Popular</option>
                  <option value="trending">Trending</option>
                </select>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Article */}
      {featuredBlog && (
        <section className="py-12">
          <div className="container mx-auto px-4">
            <motion.div 
              className="mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
                  <Star className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Featured Article</h2>
              </div>
              <p className="text-gray-600">Don't miss our editor's pick - the most engaging content this week</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
            >
              <Link 
                to={`/blog/${featuredBlog.slug}`}
                className="group block"
              >
                <div className="bg-white/90 backdrop-blur-sm rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-200 group-hover:border-red-200">
                  <div className="grid lg:grid-cols-5 gap-0">
                    <div className="relative h-80 lg:h-full lg:col-span-3">
                      <img 
                        src={featuredBlog.image} 
                        alt={featuredBlog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent"></div>
                      
                      {/* Badges */}
                      <div className="absolute top-6 left-6 flex gap-2">
                        <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 shadow-lg">
                          <Star size={14} />
                          Featured
                        </span>
                        {featuredBlog.trending && (
                          <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 shadow-lg">
                            <Flame size={14} />
                            Trending
                          </span>
                        )}
                      </div>

                      {/* Category */}
                      <div className="absolute top-6 right-6">
                        <span className="bg-white/90 backdrop-blur-sm text-gray-700 px-3 py-1 rounded-full text-sm font-medium shadow-lg">
                          {featuredBlog.category}
                        </span>
                      </div>
                    </div>
                    
                    <div className="p-8 lg:p-12 flex flex-col justify-center lg:col-span-2">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="flex items-center text-gray-500 text-sm">
                          <Calendar size={16} className="mr-2" />
                          {new Date(featuredBlog.date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </div>
                        <div className="flex items-center text-gray-500 text-sm">
                          <Clock size={16} className="mr-2" />
                          {featuredBlog.readTime}
                        </div>
                      </div>
                      
                      <h3 className="text-2xl lg:text-4xl font-bold text-gray-900 mb-6 group-hover:text-red-600 transition-colors leading-tight">
                        {featuredBlog.title}
                      </h3>
                      
                      <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                        {featuredBlog.summary}
                      </p>

                      {/* Stats */}
                      <div className="flex items-center gap-6 mb-8 text-gray-500 text-sm">
                        <div className="flex items-center gap-1">
                          <Eye size={16} />
                          <span>{(featuredBlog.views / 1000).toFixed(1)}K views</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Heart size={16} />
                          <span>{featuredBlog.likes} likes</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MessageCircle size={16} />
                          <span>{featuredBlog.comments} comments</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center text-red-500 font-bold text-lg group-hover:text-red-600 transition-colors">
                        <span>Read Full Article</span>
                        <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-2 transition-transform" />
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* Articles Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div 
            className="flex justify-between items-center mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {selectedCategory === 'all' ? 'All Articles' : categories.find(c => c.id === selectedCategory)?.name}
              </h2>
              <p className="text-gray-600">
                {sortedBlogs.length} article{sortedBlogs.length !== 1 ? 's' : ''} found
              </p>
            </div>
            
            {/* Results Summary */}
            {searchTerm && (
              <div className="text-right">
                <p className="text-sm text-gray-500">
                  Searching for "<span className="font-medium text-gray-700">{searchTerm}</span>"
                </p>
              </div>
            )}
          </motion.div>

          <AnimatePresence mode="wait">
            {sortedBlogs.length > 0 ? (
              <motion.div 
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                key={`${selectedCategory}-${sortBy}`}
              >
                {sortedBlogs.map((blog, index) => (
                  <motion.div
                    key={blog.slug}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -30 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    whileHover={{ y: -8 }}
                  >
                    <Link 
                      to={`/blog/${blog.slug}`}
                      className="group block h-full"
                    >
                      <article className="bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-200 h-full flex flex-col group-hover:border-red-200">
                        <div className="relative h-48 overflow-hidden">
                          <img 
                            src={blog.image} 
                            alt={blog.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                          
                          {/* Badges */}
                          <div className="absolute top-4 left-4 flex gap-2">
                            {blog.trending && (
                              <span className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                                <Flame size={12} />
                                Trending
                              </span>
                            )}
                            {blog.featured && (
                              <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                                <Star size={12} />
                                Featured
                              </span>
                            )}
                          </div>

                          {/* Category Badge */}
                          <div className="absolute top-4 right-4">
                            <span className="bg-white/90 backdrop-blur-sm text-gray-700 px-3 py-1 rounded-full text-xs font-medium">
                              {blog.category}
                            </span>
                          </div>
                          
                          {/* Meta Info */}
                          <div className="absolute bottom-4 left-4 text-white">
                            <div className="flex items-center text-xs text-gray-200 mb-2">
                              <Calendar size={12} className="mr-1" />
                              {new Date(blog.date).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric' 
                              })}
                              <Clock size={12} className="ml-3 mr-1" />
                              {blog.readTime}
                            </div>
                          </div>
                        </div>
                        
                        <div className="p-6 flex-1 flex flex-col">
                          <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-red-600 transition-colors line-clamp-2 flex-shrink-0">
                            {blog.title}
                          </h3>
                          <p className="text-gray-600 mb-4 line-clamp-3 flex-1">
                            {blog.summary}
                          </p>
                          
                          {/* Stats and Actions */}
                          <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
                            <div className="flex items-center gap-4 text-gray-500 text-sm">
                              <div className="flex items-center gap-1">
                                <Eye size={14} />
                                <span>{(blog.views / 1000).toFixed(1)}K</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Heart size={14} />
                                <span>{blog.likes}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <MessageCircle size={14} />
                                <span>{blog.comments}</span>
                              </div>
                            </div>
                            
                            <div className="flex items-center text-red-500 font-semibold group-hover:text-red-600 transition-colors">
                              <span className="text-sm">Read</span>
                              <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </div>
                          </div>
                        </div>
                      </article>
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <motion.div 
                className="text-center py-16"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
              >
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Search className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-2xl font-bold text-gray-700 mb-3">No articles found</h3>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  {searchTerm 
                    ? `We couldn't find any articles matching "${searchTerm}". Try different keywords or browse all articles.`
                    : "No articles match your current filters. Try adjusting your search criteria."
                  }
                </p>
                <Button 
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('all');
                  }}
                  className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
                >
                  Clear Filters
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Newsletter Section */}
      {/* <section className="py-20 bg-gradient-to-r from-red-500 to-orange-500">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Stay Updated with Dailzy
            </h2>
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
              Get the latest insights about quick commerce, local business stories, and delivery innovations delivered to your inbox
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-3 rounded-xl border-0 focus:ring-2 focus:ring-white focus:outline-none"
              />
              <Button className="bg-white text-red-500 hover:bg-gray-100 px-8 py-3 rounded-xl font-semibold">
                Subscribe
              </Button>
            </div>
          </motion.div>
        </div>
      </section> */}
    </div>
  );
};

export default Blog;
